import React from 'react';
import { 
  Phone, 
  Mail, 
  MapPin,
  Facebook,
  Instagram,
  Linkedin
} from 'lucide-react';

export const Footer = () => {
  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="container py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--secondary-brown)' }}>
                <span className="text-white font-bold text-xl">GR</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold uppercase tracking-tight" style={{ color: 'var(--secondary-brown)' }}>
                  GymRatRehab
                </h3>
                <p className="text-sm text-gray-400 uppercase tracking-wide">
                  Physical Therapy
                </p>
              </div>
            </div>
            
            <p className="text-lg font-semibold mb-4 uppercase tracking-wide" style={{ color: 'var(--brand-primary)' }}>
              Strength, Mobility, Function, Life.
            </p>
            
            <p className="text-gray-400 mb-6 max-w-md">
              Expert physical therapy for sports injuries and orthopedic conditions. 
              Serving the Southeast Valley: Apache Junction, Chandler, Gilbert, Mesa, Queen Creek, and San Tan Valley. Located inside <a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a>.
            </p>

            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/gymratrehab" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:text-black transition-colors"
                style={{ ':hover': { backgroundColor: 'var(--brand-primary)' } }}
                aria-label="Visit GymRatRehab on Facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://www.instagram.com/gymratrehab" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:text-black transition-colors"
                style={{ ':hover': { backgroundColor: 'var(--brand-primary)' } }}
                aria-label="Follow GymRatRehab on Instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:text-black transition-colors"
                style={{ ':hover': { backgroundColor: 'var(--brand-primary)' } }}
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6 uppercase tracking-wide" style={{ color: 'var(--brand-primary)' }}>
              Services
            </h4>
            <ul className="space-y-3">
              <li><a href="/services" className="text-gray-400 transition-colors" style={{ ':hover': { color: 'var(--brand-primary)' } }}>Sports Injury Recovery</a></li>
              <li><a href="/services" className="text-gray-400 transition-colors" style={{ ':hover': { color: 'var(--brand-primary)' } }}>Orthopedic Rehabilitation</a></li>
              <li><a href="/services" className="text-gray-400 transition-colors" style={{ ':hover': { color: 'var(--brand-primary)' } }}>Manual Therapy</a></li>
              <li><a href="/blog/vertigo-and-dizziness-care" className="text-gray-400 transition-colors" style={{ ':hover': { color: 'var(--brand-primary)' } }}>Vertigo & Dizziness</a></li>
              
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 uppercase tracking-wide" style={{ color: 'var(--brand-primary)' }}>
              Contact
            </h4>
            <div className="space-y-4">
              <a href="/contact?focus=contact" className="flex items-center space-x-3">
                <Phone style={{ color: 'var(--brand-primary)' }} size={16} />
                <span className="text-gray-400">Schedule Consultation</span>
              </a>
              <div className="flex items-center space-x-3">
                <Mail style={{ color: 'var(--brand-primary)' }} size={16} />
                <span className="text-gray-400">Info@gymratrehab.com</span>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="mt-1" style={{ color: 'var(--brand-primary)' }} size={16} />
                <div className="text-gray-400">
                  <div><a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a></div>
                  <div>Gilbert, Arizona</div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-900 rounded-xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <h5 className="font-semibold mb-2" style={{ color: 'var(--brand-primary)' }}>Business Hours</h5>
              <p className="text-sm text-gray-300 mb-2">Available by appointment only</p>
              <div className="text-sm text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span>Tues / Thur</span>
                  <span>2:00 PM–5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Mon / Wed / Fri–Sun</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span>. All rights reserved. | Douglas Mitchell, PT, DPT
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-lime-400 transition-colors">Privacy Policy</a>
              <a href="/terms-of-service" className="text-gray-400 hover:text-lime-400 transition-colors">Terms of Service</a>
              <a href="/hipaa-notice" className="text-gray-400 hover:text-lime-400 transition-colors">HIPAA Notice</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};